import { useMemo } from 'react';
import { cn } from '../../utils/cn';
import { borderRadius } from '../../tokens/spacing';
import { transitions, reducedMotionCSS } from '../../utils/motion';

export interface PaginationProps {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  siblingCount?: number;
  className?: string;
}

function range(start: number, end: number): number[] {
  const length = end - start + 1;
  return Array.from({ length }, (_, i) => start + i);
}

function getPageNumbers(current: number, total: number, siblingCount: number): (number | 'ellipsis')[] {
  if (total <= 7) {
    return range(1, total);
  }

  const leftSiblingIndex = Math.max(current - siblingCount, 1);
  const rightSiblingIndex = Math.min(current + siblingCount, total);

  const showLeftEllipsis = leftSiblingIndex > 2;
  const showRightEllipsis = rightSiblingIndex < total - 1;

  if (!showLeftEllipsis && showRightEllipsis) {
    const leftItemCount = 3 + 2 * siblingCount;
    const leftRange = range(1, leftItemCount);
    return [...leftRange, 'ellipsis', total];
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    const rightItemCount = 3 + 2 * siblingCount;
    const rightRange = range(total - rightItemCount + 1, total);
    return [1, 'ellipsis', ...rightRange];
  }

  const middleRange = range(leftSiblingIndex, rightSiblingIndex);
  return [1, 'ellipsis', ...middleRange, 'ellipsis', total];
}

export function Pagination({ page, totalPages, onChange, siblingCount = 1, className }: PaginationProps): JSX.Element | null {
  const pages = useMemo(() => getPageNumbers(page, totalPages, siblingCount), [page, totalPages, siblingCount]);

  if (totalPages <= 1) return null;

  const btnStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '36px',
    height: '36px',
    padding: '0 0.5rem',
    border: '1px solid #c4c6cf',
    background: '#ffffff',
    borderRadius: borderRadius.sm,
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: '#191c1e',
    fontFamily: 'inherit',
    transition: `background ${transitions.fast}, border-color ${transitions.fast}`,
    lineHeight: 1,
  };

  const activeBtnStyle: React.CSSProperties = {
    ...btnStyle,
    background: '#002045',
    borderColor: '#002045',
    color: '#ffffff',
    fontWeight: 600,
  };

  return (
    <nav
      className={cn('ds-pagination', className)}
      role="navigation"
      aria-label="Navegacao de paginas"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.25rem',
      }}
    >
      <button
        disabled={page <= 1}
        onClick={() => onChange(1)}
        aria-label="Primeira pagina"
        style={btnStyle}
        className="ds-pagination__btn"
      >
        &laquo;
      </button>
      <button
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        aria-label="Pagina anterior"
        style={btnStyle}
        className="ds-pagination__btn"
      >
        &lsaquo;
      </button>

      {pages.map((p, idx) => {
        if (p === 'ellipsis') {
          return (
            <span
              key={`ellipsis-${idx}`}
              style={{ padding: '0 0.25rem', color: '#43474e', fontSize: '0.875rem' }}
              aria-hidden="true"
            >
              &hellip;
            </span>
          );
        }

        const isActive = p === page;
        return (
          <button
            key={p}
            onClick={() => onChange(p)}
            aria-current={isActive ? 'page' : undefined}
            aria-label={`Pagina ${p}`}
            style={isActive ? activeBtnStyle : btnStyle}
            className={cn('ds-pagination__btn', isActive && 'ds-pagination__btn--active')}
          >
            {p}
          </button>
        );
      })}

      <button
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
        aria-label="Proxima pagina"
        style={btnStyle}
        className="ds-pagination__btn"
      >
        &rsaquo;
      </button>
      <button
        disabled={page >= totalPages}
        onClick={() => onChange(totalPages)}
        aria-label="Ultima pagina"
        style={btnStyle}
        className="ds-pagination__btn"
      >
        &raquo;
      </button>

      <style>{`
        .ds-pagination__btn:hover:not(:disabled) {
          background: #f2f4f6;
          border-color: #c4c6cf;
        }
        .ds-pagination__btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .ds-pagination__btn:focus-visible {
          outline: 2px solid #002045;
          outline-offset: 2px;
        }
        .ds-pagination__btn--active:hover {
          background: #001b3c !important;
          border-color: #001b3c !important;
        }
        ${reducedMotionCSS('.ds-pagination__btn')}
      `}</style>
    </nav>
  );
}

Pagination.displayName = 'Pagination';
